import { Mic, Plus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { AgendaGenerationTranscriptionMetadataPurpose } from "@elation/api-client/src/stitch-web";

import {
  useTourGuideStep,
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_ID,
  VISIT_TOUR_STEP_IDS,
  VisitTour,
} from "@src/features/tourguide";
import { VoiceInputButton } from "@src/features/voice-input";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";

import { GapListDropzoneV2 } from "../GapListDropzone/GapListDropzone";

const UNHANDLED_TOUR_ACTION_ERROR =
  "Tour add agenda item action was not handled";

export interface AgendaAddItemControlsProps {
  canEdit: boolean;
  encounterId?: number;
  patientId?: number;
  isGapListUploading?: boolean;
  onCreateAgendaItem: (description: string) => Promise<void>;
  onGapListFilesSelected: (files: File[]) => void;
}

export interface AgendaAddItemControlsViewProps {
  canEdit: boolean;
  encounterId?: number;
  isGapListUploading?: boolean;
  patientId?: number;
  onSubmit: (description: string) => Promise<void>;
  onGapListFilesSelected: (files: File[]) => void;
}

export function AgendaAddItemControlsView({
  canEdit,
  encounterId,
  isGapListUploading = false,
  patientId,
  onSubmit,
  onGapListFilesSelected,
}: AgendaAddItemControlsViewProps) {
  const [draft, setDraft] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const description = draft.trim();
    if (!description || !canEdit) return;

    setDraft("");
    void onSubmit(description).catch(() => {
      setDraft((current) => current || description);
    });
  };

  return (
    <div className="shrink-0 border-border border-t bg-background px-[18px] pb-5 pt-3 shadow-[0_-6px_20px_rgba(20,23,26,0.05)]">
      <VisitTour.AddAgendaItemStep>
        <form className="flex gap-2" onSubmit={handleSubmit}>
          <Input
            aria-label="Agenda item"
            className="h-9 rounded-[9px] bg-background text-[13px]"
            disabled={!canEdit}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={
              canEdit
                ? "Type an agenda item"
                : "Add a patient to start the agenda"
            }
            type="text"
            value={draft}
          />
          <Button
            className="h-9 rounded-[9px] px-3 text-[12.5px] font-semibold"
            disabled={!canEdit || !draft.trim()}
            type="submit"
          >
            <Plus aria-hidden="true" className="size-3.5" />
            Add
          </Button>
        </form>
      </VisitTour.AddAgendaItemStep>

      <VisitTour.FasterInputsStep>
        <div className="mt-3 flex items-center gap-3 text-[11px] font-medium uppercase text-muted-foreground">
          <span>or</span>
          <GapListDropzoneV2
            disabled={!canEdit}
            isUploading={isGapListUploading}
            onFilesSelected={onGapListFilesSelected}
          />
          {patientId != null ? (
            <VoiceInputButton
              buttonClassName="normal-case"
              config={{
                purpose:
                  AgendaGenerationTranscriptionMetadataPurpose.agendagenerate,
                metadata: {
                  patientId,
                  ...(encounterId != null ? { encounterId } : {}),
                },
              }}
              disabled={!canEdit}
              iconClassName="size-3.5"
              labels={{ processing: "Creating agenda items..." }}
              onTranscript={() => {
                toast.success("Creating agenda items from voice");
              }}
            />
          ) : (
            <span className="inline-flex items-center gap-1.5 px-1.5 py-1 text-[11px] font-medium normal-case opacity-45">
              <Mic aria-hidden="true" className="size-3.5" />
              Speak
            </span>
          )}
        </div>
      </VisitTour.FasterInputsStep>
    </div>
  );
}

export function AgendaAddItemControls({
  canEdit,
  encounterId,
  patientId,
  isGapListUploading = false,
  onCreateAgendaItem,
  onGapListFilesSelected,
}: AgendaAddItemControlsProps) {
  const addAgendaItemTourStep = useTourGuideStep(
    VISIT_TOUR_ID,
    VISIT_TOUR_STEP_IDS.addAgendaItem,
  );
  const isVisitTourEncounter = encounterId === VISIT_TOUR_ENCOUNTER_ID;

  if (isVisitTourEncounter) {
    return (
      <AgendaAddItemControlsView
        canEdit={canEdit}
        encounterId={encounterId}
        isGapListUploading={isGapListUploading}
        patientId={patientId}
        onSubmit={async (description) => {
          const handled = await addAgendaItemTourStep.actions.addAgendaItem?.({
            encounterId,
            description,
          });

          if (!handled) {
            throw new Error(UNHANDLED_TOUR_ACTION_ERROR);
          }
        }}
        onGapListFilesSelected={onGapListFilesSelected}
      />
    );
  }

  return (
    <AgendaAddItemControlsView
      canEdit={canEdit}
      encounterId={encounterId}
      isGapListUploading={isGapListUploading}
      patientId={patientId}
      onSubmit={onCreateAgendaItem}
      onGapListFilesSelected={onGapListFilesSelected}
    />
  );
}
